// SettingsCoverPane.jsx

import React, { useRef, useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';

// Utilities
import { PhotoShape } from 'utilities/Enums';
import { notifyBaseLimitError } from 'utilities/baseLimitFeedback';

// Services
import { clearBaseCover, uploadBaseCover } from 'services/bases/BaseCoverService';

// Components
import SettingsModalPaneHeader from './SettingsModalPaneHeader';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import Button from 'components/button/Button';
import BaseCover from 'components/basecover/BaseCover';

/**
 * SettingsCoverPane
 *
 * Renders upload and remove controls for the active base sidebar cover image.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {object} base - Active base model.
 * @param {boolean} canEditCover - Whether the current user can edit space cover.
 *
 * @returns {ReactNode} The rendered cover settings pane.
 */
const SettingsCoverPane = React.memo(({
    theme,
    base,
    canEditCover,
}) => {

    // ----- CONTEXT ----- //

    // Base Context

    const { setActiveBase } = useBase();

    // ----- REFS ----- //

    // File Input Reference

    const fileInputRef = useRef(null);

    // ----- STATE ----- //

    // Upload Pending State

    const [isUploading, setIsUploading] = useState(false);

    // Remove Pending State
    const [isRemoving, setIsRemoving] = useState(false);

    // Error Message State
    const [errorMessage, setErrorMessage] = useState('');

    // Status Message State
    const [statusMessage, setStatusMessage] = useState('');

    // Indicates whether a persisted sidebar cover exists.
    const hasCover = typeof base?.sidebarCoverPhotoUrl === 'string' && base.sidebarCoverPhotoUrl.trim().length > 0;

    // ----- HANDLERS ----- //

    /**
     * Opens the hidden file picker when editing is allowed.
     */

    const handleChooseClick = () => {
        if (!canEditCover || isUploading || isRemoving) return;
        fileInputRef.current?.click();
    };

    /**
     * Uploads the selected space cover image.
     *
     * @param {object} event - Input change event.
     */
    const handleFileChange = async (event) => {
        const file = event.target?.files?.[0] || null;
        event.target.value = '';

        if (!file || !base?.key) return;

        setErrorMessage('');
        setStatusMessage('Uploading base cover...');
        setIsUploading(true);

        try {
            const nextCoverPhotoUrl = await uploadBaseCover({
                baseKey: base.key,
                file,
                previousCoverPath: base.sidebarCoverPhotoUrl || null,
            });
            setActiveBase((previous) => {
                if (!previous || previous.key !== base.key) return previous;
                return {
                    ...previous,
                    sidebarCoverPhotoUrl: nextCoverPhotoUrl,
                };
            });
            setStatusMessage('Space cover updated.');
        } catch (error) {
            notifyBaseLimitError({
                error,
                baseKey: base.key,
            });
            setErrorMessage(error?.message || 'Failed to upload space cover.');
            setStatusMessage('');
        } finally {
            setIsUploading(false);
        }
    };

    /**
     * Clears the persisted space cover path.
     */
    const handleRemoveClick = async () => {
        if (!canEditCover || !base?.key || !hasCover || isUploading || isRemoving) return;

        setErrorMessage('');
        setStatusMessage('Removing space cover...');
        setIsRemoving(true);

        try {
            await clearBaseCover({
                baseKey: base.key,
                previousCoverPath: base.sidebarCoverPhotoUrl || null,
            });
            setActiveBase((previous) => {
                if (!previous || previous.key !== base.key) return previous;
                return {
                    ...previous,
                    sidebarCoverPhotoUrl: null,
                };
            });
            setStatusMessage('Space cover removed.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to remove space cover.');
            setStatusMessage('');
        } finally {
            setIsRemoving(false);
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <SettingsModalPaneHeader
              title="Cover Photo"
              subtitle="Wide space cover used at the top of the sidebar."
            />

            <SettingsModalPaneContents>
                <div
                  className="settings-modal-cover-card"
                  style={{
                    borderColor: theme.backgroundHover,
                  }}
                >
                    <div
                      className="settings-modal-cover-preview"
                      style={{
                        width: '240px',
                      }}
                    >
                        <BaseCover
                          base={base}
                          assetType="cover"
                          borderWidth={0}
                          borderRadius={10}
                          height={160}
                          shape={PhotoShape.SQUARE}
                          width="100%"
                        />
                    </div>

                    <div className="settings-modal-cover-meta">
                        <div
                          className="settings-modal-setting-label"
                          style={{
                            color: theme.foreground,
                          }}
                        >
                            Sidebar cover
                        </div>
                        <div
                          className="settings-modal-setting-description"
                          style={{
                            color: theme.foregroundMuted,
                          }}
                        >
                            Images keep their rectangular aspect ratio and are cropped at render time for the sidebar header.
                        </div>
                    </div>

                    <div className="settings-modal-cover-actions">
                        <Button
                          iconKey={hasCover ? 'image' : 'upload'}
                          label={hasCover ? 'Replace' : 'Upload'}
                          enabled={canEditCover && !isUploading && !isRemoving}
                          onClick={handleChooseClick}
                        />

                        <Button
                          iconKey="delete"
                          label="Remove"
                          enabled={canEditCover && hasCover && !isUploading && !isRemoving}
                          onClick={handleRemoveClick}
                        />
                    </div>
                </div>

                {!canEditCover && (
                    <div
                      className="settings-modal-note"
                      style={{
                        color: theme.foregroundMuted,
                      }}
                    >
                        Only admins and owners can update the space cover.
                    </div>
                )}

                {(isUploading || isRemoving || statusMessage) && !errorMessage && (
                    <div className="settings-modal-status">{statusMessage}</div>
                )}

                {errorMessage && (
                    <div className="settings-modal-error">{errorMessage}</div>
                )}

                <input
                  style={{
                    display: 'none',
                  }}
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
            </SettingsModalPaneContents>
        </>
    );
});

// Dev Tools
SettingsCoverPane.displayName = 'SettingsCoverPane';

export default SettingsCoverPane;
